import React, { useEffect, useRef, useState } from "react";
import { Search, Filter, SlidersVertical, ChevronDown, Check } from "lucide-react";
import { Input } from "../../@/components/ui/input";
import { Button } from "../../@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../../@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../@/components/ui/select";

export default function TableToolbar({
  searchTerm,
  onSearchChange,
  dropdowns = [],
  dropdownPosition = "before",
  filters = [],
  textFilters = [],
  sorts = [],
  onApplyFilters,
  onApplySort,
}) {
  const arraysEqual = (a, b) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };

  const [sortOpen, setSortOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [customOpenIndex, setCustomOpenIndex] = useState(null);

  // Internal state for filters and sorts
  const [internalFilters, setInternalFilters] = useState(filters.map(f => f.value));
  const [internalSorts, setInternalSorts] = useState(sorts.map(s => s.value));
  const [internalDropdowns, setInternalDropdowns] = useState(dropdowns.map(d => d.value));
  const [dropdownSearch, setDropdownSearch] = useState(dropdowns.map(() => ""));
  const [internalSearch, setInternalSearch] = useState(searchTerm);

  // NOTE: Many pages pass `filters`/`sorts`/`dropdowns` as inline arrays.
  // Using those arrays directly as deps causes effects to run every render.
  const filterValueSignature = (filters || []).map((f) => String(f?.value ?? "")).join("|");
  const sortValueSignature = (sorts || []).map((s) => String(s?.value ?? "")).join("|");
  const dropdownValueSignature = (dropdowns || []).map((d) => String(d?.value ?? "")).join("|");
  const dropdownKeySignature = (dropdowns || []).map((d) => String(d?.key ?? d?.label ?? "")).join("|");
  const prevDropdownKeySignatureRef = useRef(dropdownKeySignature);

  useEffect(() => {
    const next = (filters || []).map((f) => f.value);
    setInternalFilters((prev) => (arraysEqual(prev, next) ? prev : next));
  }, [filterValueSignature]);
  useEffect(() => {
    const next = (sorts || []).map((s) => s.value);
    setInternalSorts((prev) => (arraysEqual(prev, next) ? prev : next));
  }, [sortValueSignature]);
  useEffect(() => {
    const next = (dropdowns || []).map((d) => d.value);
    setInternalDropdowns((prev) => (arraysEqual(prev, next) ? prev : next));

    // Only reset search/open state when the dropdown set itself changes (keys/labels).
    if (prevDropdownKeySignatureRef.current !== dropdownKeySignature) {
      setDropdownSearch((dropdowns || []).map(() => ""));
      setCustomOpenIndex(null);
      prevDropdownKeySignatureRef.current = dropdownKeySignature;
    }
  }, [dropdownValueSignature, dropdownKeySignature]);
  useEffect(() => {
    setInternalSearch(searchTerm);
  }, [searchTerm]);

  const handleDropdownChange = (index, value) => {
    setInternalDropdowns((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
    if (dropdowns[index] && typeof dropdowns[index].onChange === "function") {
      dropdowns[index].onChange(value);
    }
    setCustomOpenIndex(null);
  };

  const clearDropdown = (index) => {
    if (dropdowns[index] && typeof dropdowns[index].onClear === "function") {
      dropdowns[index].onClear();
    }
    setCustomOpenIndex(null);
  };

  const handleFilterChange = (index, value) => {
    setInternalFilters((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
    if (filters[index] && typeof filters[index].onChange === "function") {
      filters[index].onChange(value);
    }
  };

  const handleSortChange = (index, value) => {
    setInternalSorts((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
    if (sorts[index] && typeof sorts[index].onChange === "function") {
      sorts[index].onChange(value);
    }
  };

  // Apply filters to parent
  const handleApplyFilters = () => {
    if (onApplyFilters) onApplyFilters();
    setFilterOpen(false);
  };

  // Clear filters in both internal and parent state
  const clearFilters = () => {
    filters.forEach((filter, i) => {
      if (filter.onClear) filter.onClear();
    });
    setInternalFilters(filters.map(f => f.value));

    (textFilters || []).forEach((tf) => {
      if (typeof tf?.onClear === "function") tf.onClear();
      else if (typeof tf?.onChange === "function") tf.onChange("");
    });
  };

  // Apply sorts to parent
  const handleApplySort = () => {
    if (onApplySort) onApplySort();
    setSortOpen(false);
  };

  // Clear sorts in both internal and parent state
  const clearSort = () => {
    sorts.forEach((sort, i) => {
      if (sort.onClear) sort.onClear();
    });
    setInternalSorts(sorts.map(s => s.value));
  };


  return (
    <div className="flex items-center gap-4 mb-6 md:justify-between">
      {/* Search Input */}
      <div className="relative flex-1 md:max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-(--neutral-400) w-4 h-4" />
        <Input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10 pr-4 py-2 w-full font-body rounded-md border border-(--neutral-300) bg-(--surface-panel) text-(--neutral-900) placeholder:text-(--neutral-500)"
        />
      </div>

      {/* Filter and Sort Controls */}
      <div className="flex items-center gap-2 shrink-0">
        {/* Custom Dropdown Buttons (separate from filters/sorts) */}
        {dropdownPosition === "before" && dropdowns.length > 0 && dropdowns.map((dd, index) => {
          const Icon = dd.icon;
          const value = internalDropdowns[index];
          const searchValue = dropdownSearch[index] || "";
          const options = Array.isArray(dd.options) ? dd.options : [];
          const filteredOptions = dd.searchable
            ? options.filter((o) => String(o.label || "").toLowerCase().includes(searchValue.toLowerCase()))
            : options;

          return (
            <DropdownMenu
              key={dd.key || dd.label || index}
              open={customOpenIndex === index}
              onOpenChange={(open) => setCustomOpenIndex(open ? index : null)}
            >
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size=""
                  className="flex items-center gap-2 px-4 py-2 rounded-md font-body border-(--neutral-300) text-(--neutral-900) bg-(--surface-panel)"
                >
                  <span className="hidden md:inline">{dd.buttonLabel || dd.label}</span>
                  {Icon ? <Icon className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 p-4 space-y-3">
                <div className="text-sm font-medium font-heading text-(--neutral-800)">
                  {dd.label}
                </div>

                {dd.searchable && (
                  <Input
                    type="text"
                    placeholder={dd.searchPlaceholder || `Search ${dd.label.toLowerCase()}...`}
                    value={searchValue}
                    onChange={(e) => {
                      const next = e.target.value;
                      setDropdownSearch((prev) => {
                        const updated = [...prev];
                        updated[index] = next;
                        return updated;
                      });
                    }}
                    className="w-full font-body rounded-md border border-(--neutral-300) bg-(--surface-panel) text-(--neutral-900) placeholder:text-(--neutral-500)"
                  />
                )}

                <div className="max-h-72 overflow-y-auto rounded-md border border-(--neutral-200) bg-(--surface-base)">
                  {filteredOptions.map((opt) => {
                    const selected = value === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-(--neutral-800) hover:bg-(--neutral-100)"
                        onClick={() => handleDropdownChange(index, opt.value)}
                      >
                        <span className="pr-3">{opt.label}</span>
                        {selected && <Check className="h-4 w-4 text-(--color-primary)" />}
                      </button>
                    );
                  })}

                  {filteredOptions.length === 0 && (
                    <div className="px-3 py-2 text-sm text-(--neutral-500)">
                      No options found.
                    </div>
                  )}
                </div>

                {typeof dd.onClear === "function" && (
                  <Button
                    variant="outline"
                    size=""
                    onClick={() => clearDropdown(index)}
                    className="font-body w-full"
                  >
                    Clear
                  </Button>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        })}

        {/* Dynamic Filter Dropdown */}
        {filters.length > 0 && (
          <DropdownMenu open={filterOpen} onOpenChange={setFilterOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size=""
                className="flex items-center gap-2 px-4 py-2 rounded-md font-body border-(--neutral-300) text-(--neutral-900) bg-(--surface-panel)"
              >
                <span className="hidden md:inline">Filter by</span>
                <Filter className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 p-4 space-y-4">
              {/* Dynamic Filters */}
              {filters.map((filter, index) => (
                <div key={index} className="space-y-2">
                  <label className="text-sm font-medium font-heading text-(--neutral-800)">
                    {filter.label}
                  </label>
                  <Select value={internalFilters[index]} onValueChange={(value) => handleFilterChange(index, value)}>
                    <SelectTrigger className="w-full text-(--neutral-900)">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {filter.options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}

              {/* Free-text filters (backend params) */}
              {(textFilters || []).map((tf, idx) => (
                <div key={tf.key || tf.label || idx} className="space-y-2">
                  <label className="text-sm font-medium font-heading text-(--neutral-800)">
                    {tf.label}
                  </label>
                  <Input
                    type={tf.type || "text"}
                    placeholder={tf.placeholder || ""}
                    value={tf.value || ""}
                    onChange={(e) => {
                      if (typeof tf.onChange === "function") tf.onChange(e.target.value);
                    }}
                    className="w-full font-body rounded-md border border-(--neutral-300) bg-(--surface-panel) text-(--neutral-900) placeholder:text-(--neutral-500)"
                  />

                  {typeof tf.onClear === "function" && String(tf.value || "").trim() !== "" && (
                    <Button
                      variant="outline"
                      size=""
                      onClick={() => tf.onClear()}
                      className="font-body w-full"
                    >
                      Clear
                    </Button>
                  )}
                </div>
              ))}

              {/* Apply Filters Button */}
              <div className="flex flex-col gap-2 pt-2">
                <Button
                  variant=""
                  size=""
                  onClick={handleApplyFilters}
                  className="w-full"
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outline"
                  size=""
                  onClick={clearFilters}
                  className="w-full"
                >
                  Clear
                </Button>

              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Dynamic Sort Dropdown */}
        {sorts.length > 0 && (
          <DropdownMenu open={sortOpen} onOpenChange={setSortOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size=""
                 className="flex items-center gap-2 px-4 py-2 rounded-md font-body border-(--neutral-300) text-(--neutral-900) bg-(--surface-panel)"
              >
                <span className="hidden md:inline">Sort by</span>
                <SlidersVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 p-4 space-y-4">
              {/* Dynamic Sorts */}
              {sorts.map((sort, index) => (
                <div key={index} className="space-y-2">
                  <label className="text-sm font-medium font-heading text-(--neutral-800)">
                    {sort.label}
                  </label>
                  <Select value={internalSorts[index]} onValueChange={(value) => handleSortChange(index, value)}>
                    <SelectTrigger className="w-full text-(--neutral-900)">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {sort.options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}

              {/* Apply Sort Button */}
              <div className="flex flex-col gap-2 pt-2">
                <Button
                  variant=""
                  size=""
                  onClick={handleApplySort}
                  className="w-full"
                >
                  Apply Sort
                </Button>
                <Button
                  variant="outline"
                  size=""
                  onClick={clearSort}
                  className="w-full"
                >
                  Clear
                </Button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Custom Dropdown Buttons after filters/sorts */}
        {dropdownPosition === "after" && dropdowns.length > 0 && dropdowns.map((dd, index) => {
          const Icon = dd.icon;
          const value = internalDropdowns[index];
          const searchValue = dropdownSearch[index] || "";
          const options = Array.isArray(dd.options) ? dd.options : [];
          const filteredOptions = dd.searchable
            ? options.filter((o) => String(o.label || "").toLowerCase().includes(searchValue.toLowerCase()))
            : options;

          return (
            <DropdownMenu
              key={dd.key || dd.label || index}
              open={customOpenIndex === index}
              onOpenChange={(open) => setCustomOpenIndex(open ? index : null)}
            >
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size=""
                  className="flex items-center gap-2 px-4 py-2 rounded-md font-body border-(--neutral-300) text-(--neutral-900) bg-(--surface-panel)"
                >
                  <span className="hidden md:inline">{dd.buttonLabel || dd.label}</span>
                  {Icon ? <Icon className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 p-4 space-y-3">
                <div className="text-sm font-medium font-heading text-(--neutral-800)">
                  {dd.label}
                </div>

                {dd.searchable && (
                  <Input
                    type="text"
                    placeholder={dd.searchPlaceholder || `Search ${dd.label.toLowerCase()}...`}
                    value={searchValue}
                    onChange={(e) => {
                      const next = e.target.value;
                      setDropdownSearch((prev) => {
                        const updated = [...prev];
                        updated[index] = next;
                        return updated;
                      });
                    }}
                    className="w-full font-body rounded-md border border-(--neutral-300) bg-(--surface-panel) text-(--neutral-900) placeholder:text-(--neutral-500)"
                  />
                )}

                <div className="max-h-72 overflow-y-auto rounded-md border border-(--neutral-200) bg-(--surface-base)">
                  {filteredOptions.map((opt) => {
                    const selected = value === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-(--neutral-800) hover:bg-(--neutral-100)"
                        onClick={() => handleDropdownChange(index, opt.value)}
                      >
                        <span className="pr-3">{opt.label}</span>
                        {selected && <Check className="h-4 w-4 text-(--color-primary)" />}
                      </button>
                    );
                  })}

                  {filteredOptions.length === 0 && (
                    <div className="px-3 py-2 text-sm text-(--neutral-500)">
                      No options found.
                    </div>
                  )}
                </div>

                {typeof dd.onClear === "function" && (
                  <Button
                    variant="outline"
                    size=""
                    onClick={() => clearDropdown(index)}
                    className="w-full"
                  >
                    Clear
                  </Button>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        })}
      </div>
    </div>
  );
}